import Constant from '@/constant'
import presentationGetters from './presentation'
import slideActiveGetters from './slideActive'
import currentUserGetters from './currentUser'
import currentTeamGetters from './currentTeam'
import pricingGetters from './pricing'
import surveyGetters from './survey'
import configs from '@/config'
import pageGetter from './pageGetter'
import slides from './slides'
import googleSlidesAddOnGetters from './googleSlidesAddOn'
import * as plan from './plan'
import { ROUTE_NAMES } from '@/constant/routes'
import powerpointGetters from './powerpoint'
import getSelectedSlides from './selectedSlides'

const getSlideOptionsActive = (state) => {
  return state.slideActive.SlideOptions
}

const selectedFolder = (state) => {
  if (!state.folders || !state.folders.length) return undefined
  return state.folders.find((folder) => folder.folderId === state.selections.selectedFolderId)
}

const getPresenterLocale = (state) => {
  return state.presenterLocale || 'en'
}

// AHA-46429: single source of truth for "an export job is running" — used
// by editor header menus (disable Download items) and DownloadSlideSection
// (loading card). Both PDF and image share the same UI-blocking semantic.
const isExportingSlides = (state) => Boolean(state.loadingExportImage || state.loadingExportPdfV2)

// Merge one attribute across two slides for the multi-select `selectedSlide` view:
// a shared value shows through, a differing one collapses to a "mixed" sentinel.
const mergeSlideAttribute = (prop, a, b) => {
  if (a[prop] === undefined) return b[prop]
  if (prop === 'backgroundImage' || prop === 'audioLink') {
    return (a[prop] && a[prop].name) === (b[prop] && b[prop].name) ? b[prop] : null
  }
  if (prop === 'backgroundImageCropperData') return a[prop] || b[prop]
  if (prop === 'visibility') {
    return parseInt(a[prop], 10) === parseInt(b[prop], 10) ? parseInt(b[prop], 10) : 20
  }
  return a[prop] === b[prop] ? b[prop] : null
}

export default {
  ...presentationGetters,
  ...slideActiveGetters,
  ...currentUserGetters,
  ...surveyGetters,
  ...pricingGetters,
  ...currentTeamGetters,
  ...pageGetter,
  ...slides,
  ...plan,
  ...googleSlidesAddOnGetters,
  ...powerpointGetters,
  getSlideOptionsActive,
  getPresenterLocale,
  isExportingSlides,
  selectedFolder,
  visibleSlides: (_, __, ___, rootGetters) => {
    return rootGetters['skipSlide/visibleSlides']
  },
  currentSlideIndex: (state, getters) => {
    const { visibleSlides } = getters
    return visibleSlides && visibleSlides.findIndex(
      (s) => s.id === state.slideActive.id,
    )
  },
  selectedSlides: (state) => {
    return getSelectedSlides(state)
  },
  selectedSlide: (state) => {
    return getSelectedSlides(state)
      .reduce((a, b) => {
        const c = {}
        Constant.commonSlideAttributes.forEach((prop) => {
          c[prop] = mergeSlideAttribute(prop, a, b)
        })
        return c
      }, {})
  },

  abTestGroup: (state) => {
    return state.currentUser && state.currentUser.id % 4
  },
  isEURegion() {
    return configs.region === 'eu'
  },
  isStagingGlobal() {
    return configs.env === 'develop'
  },
  isCanaryGlobal() {
    return window.location.hostname === 'presenter2.ahaslides.com'
  },
  isProductionGlobal() {
    return window.location.hostname === 'presenter.ahaslides.com'
  },
  isStagingEU() {
    return window.location.hostname === 'presenter.dev.ahaslides.eu'
  },
  isCanaryEU() {
    return window.location.hostname === 'presenter2.ahaslides.eu'
  },
  isProductionEU() {
    return window.location.hostname === 'presenter.ahaslides.eu'
  },
  // eslint-disable-next-line no-unused-vars
  isPresentationEditorRoute: (_state) => (route) => {
    return route?.name === ROUTE_NAMES.PRESENTATION_DETAIL
  },

  hasPrevSlide(_, getters) {
    return getters.currentSlideIndex > 0
  },

  hasNextSlide(_, getters) {
    const { visibleSlides } = getters
    return visibleSlides && getters.currentSlideIndex < visibleSlides.length - 1
  },

  nextSlideId(_, getters) {
    const { visibleSlides } = getters
    return visibleSlides[getters.currentSlideIndex + 1]?.id
  },

  prevSlideId(_, getters) {
    const { visibleSlides } = getters
    return visibleSlides[getters.currentSlideIndex - 1]?.id
  },
}
